import React from "react";
import Form from "react-bootstrap/Form";
import InputGroup from "react-bootstrap/InputGroup";
import styled from "styled-components";

import {
  faCheck,
  faCircleX,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Container, Row, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FlashpackItem, FlashpackType, MinMaxPair } from "../../common/api";
import { ApiContext } from "../../common/context/ApiContext";
import { ConfirmItemRemovalModal } from "./ConfirmItemRemovalModal";
import { ClientConfigContext } from "../../common/context/ClientConfigContext";
import { useLoadingIndicator } from "../../common/context/LoadingIndicatorContext";
import { RetryContext } from "../../common/context/RetryContext";
import { LessonImage } from "../../common/LessonImage";
import {
  AnalyticsContext,
  FlashpackItemSelectionCardTapEvent,
  FlashpackItemSelectionCellTapEvent,
  ViewFlashpackItemSelectionEvent,
} from "../../common/context/AnalyticsContext";
import {
  COLOR_GOLD,
  COLOR_GRAY_INACTIVE,
  COLOR_GRAY_MUTED,
  COLOR_HOT_PINK,
  COLOR_WHITE,
} from "../../common/colors";

const Subhead = styled.span`
  color: ${COLOR_GRAY_MUTED};
  font-size: 13px;
`;

const ExtraItemNote = styled.div`
  color: ${COLOR_GRAY_MUTED};
  font-size: 14px;
`;

const FlashcardThumbnailCol = styled(Col)`
  overflow: hidden;
  border: 1px solid ${COLOR_GRAY_MUTED};
  border-radius: 8px;
  height: inherit;
  background: linear-gradient(
    to bottom,
    ${COLOR_HOT_PINK},
    ${COLOR_GOLD}
  );
  color: ${COLOR_WHITE};
  font-family: "Inter";
  font-weight: 600;
`;

const ResponsiveFlashcardThumbnail = ({
  item,
  onClick,
}: {
  item: FlashpackItem;
  onClick: () => void;
}) => {
  const maxSize = 24;
  const minSize = 10;
  const parentRef = React.useRef<HTMLDivElement>(null);
  const textRef = React.useRef<HTMLSpanElement>(null);
  const [fontSize, setFontSize] = React.useState(minSize);

  const updateFontSize = React.useCallback(() => {
    if (parentRef.current && textRef.current) {
      const parentWidth = parentRef.current.offsetWidth;
      const size = parentWidth / (item.item.length * 0.7);
      const newSize = Math.min(maxSize, Math.max(minSize, size));
      setFontSize(newSize);
    }
  }, [item, parentRef, textRef, setFontSize]);

  React.useEffect(() => {
    updateFontSize();
    const handleResize = () => updateFontSize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [updateFontSize]);

  return (
    <FlashcardThumbnailCol
      xs={3}
      md={2}
      className="d-inline-block px-0 py-2 m-2 text-center"
      onClick={onClick}
      ref={parentRef}
    >
      <span
        style={{
          fontSize: `${fontSize}px`,
          lineHeight: `${fontSize}px`,
        }}
        ref={textRef}
      >
        {item.item}
      </span>
    </FlashcardThumbnailCol>
  );
};

const FlashpackItemListItem = ({
  item,
  isSelected,
  isExtraItem,
  onClick,
  useUnknownEmptyImage,
}: {
  item: FlashpackItem;
  isSelected: boolean;
  isExtraItem: boolean;
  onClick: () => void;
  useUnknownEmptyImage: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div
      className="p-2"
      style={{ borderBottom: `0.5px solid ${COLOR_GRAY_INACTIVE}`, cursor: "pointer" }}
      onClick={onClick}
    >
      <Stack direction="horizontal">
        {item.image_url && (
          <div
            style={{
              border: `1px solid ${COLOR_GRAY_INACTIVE}`,
              borderRadius: "20px",
              width: "40px",
              height: "40px",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <LessonImage
              lazy
              thumbSize="small"
              url={item.image_url}
              alt=""
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        )}
        {!item.image_url && useUnknownEmptyImage && (
          <div
            style={{
              border: `1px solid ${COLOR_GRAY_INACTIVE}`,
              borderRadius: "20px",
              height: "40px",
              width: "40px",
              fontSize: "24px",
              color: COLOR_GRAY_INACTIVE,
            }}
          >
            <center>?</center>
          </div>
        )}
        <div className="ms-3" style={{ fontWeight: 500 }}>
          {item.item}
        </div>
        {isSelected && (
          <div className="ms-auto">
            <FontAwesomeIcon icon={faCheck} />
          </div>
        )}
      </Stack>
      {isExtraItem && (
        <ExtraItemNote className="mt-2">
          {t("flashpacks_extra_item_note")}
        </ExtraItemNote>
      )}
    </div>
  );
};

interface FlashpackItemForFiltering {
  item: string;
  itemLower: string; // toLowercase'ed version of 'item', for faster searches
  image_url;
}

export const FlashpackBuilder = ({
  flashpackType,
  deckItems,
  setDeckItems,
  setInvalidDeckErrorMessage,
}: {
  flashpackType: FlashpackType;
  deckItems: FlashpackItem[];
  setDeckItems: (newDeckItems: FlashpackItem[]) => void;
  setInvalidDeckErrorMessage: (message: string | null) => void;
}) => {
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoadingIndicator(false);
  const { showRetry } = React.useContext(RetryContext);
  const { getFlashpackItems } = React.useContext(ApiContext);
  const { clientConfig } = React.useContext(ClientConfigContext);
  const [minMaxCards, setMinMaxCards] = React.useState<MinMaxPair>();
  const [allServerItems, setAllServerItems] = React.useState<
    FlashpackItemForFiltering[]
  >([]);
  const [selectableItems, setSelectableItems] = React.useState<
    FlashpackItemForFiltering[]
  >([]);
  const [searchText, setSearchText] = React.useState("");
  const [extraItem, setExtraItem] = React.useState<FlashpackItemForFiltering>();
  const [selectedWordsLowercase, setSelectedWordsLowercase] = React.useState<
    Set<string>
  >(new Set());
  // Whether to render "?" for items with empty image urls
  const [useUnknownEmptyImage, setUseUnknownEmptyImage] = React.useState(false);
  const [pendingItemRemoval, setPendingItemRemoval] =
    React.useState<FlashpackItem>();
  const { logEvent } = React.useContext(AnalyticsContext);

  React.useEffect(() => {
    logEvent(new ViewFlashpackItemSelectionEvent());
  }, [logEvent]);

  // Initialize everything once:
  // - Load flashpack items from the server
  React.useEffect(() => {
    startLoading();
    setMinMaxCards(
      clientConfig.flashpack_min_max_card_constraints[flashpackType]
    );
    getFlashpackItems(flashpackType).then((items) => {
      const newItems = items.map((item) => {
        return { ...item, itemLower: item.item.toLowerCase() };
      });
      setAllServerItems(newItems);
      setUseUnknownEmptyImage(
        items.find((item) => item.image_url) !== undefined
      );
      stopLoading();
    });
  }, [
    flashpackType,
    clientConfig,
    setMinMaxCards,
    setAllServerItems,
    getFlashpackItems,
    startLoading,
    stopLoading,
    showRetry,
  ]);

  React.useEffect(() => {
    if (minMaxCards === undefined) {
      setInvalidDeckErrorMessage(t("flashpacks_edit_unknown_type"));
    } else if (deckItems.length < minMaxCards.min) {
      setInvalidDeckErrorMessage(
        t("flashpacks_edit_less_than_min_cards", { count: minMaxCards.min })
      );
    } else if (deckItems.length > minMaxCards.max) {
      setInvalidDeckErrorMessage(
        t("flashpacks_edit_more_than_max_cards", { count: minMaxCards.max })
      );
    } else {
      setInvalidDeckErrorMessage(null);
    }

    setSelectedWordsLowercase(
      new Set(deckItems.map((item) => item.item.toLowerCase()))
    );
  }, [
    t,
    setInvalidDeckErrorMessage,
    setSelectedWordsLowercase,
    deckItems,
    minMaxCards,
  ]);

  React.useEffect(() => {
    if (!searchText) {
      setSelectableItems(allServerItems);
      setExtraItem(undefined);
    } else {
      const searchTextLower = searchText.toLowerCase();
      setSelectableItems(
        allServerItems.filter((item) =>
          item.itemLower.startsWith(searchTextLower)
        )
      );
      // If entered text isn't one of the known words,
      // add it as an extra item at the end of the list
      if (!allServerItems.find((item) => item.itemLower === searchTextLower)) {
        setExtraItem({
          item: searchText,
          itemLower: searchTextLower,
          image_url: null,
        });
      } else {
        setExtraItem(undefined);
      }
    }
  }, [setSelectableItems, setExtraItem, searchText, allServerItems]);

  const onChangedSearchText = React.useCallback(
    (value: string) => {
      const newValue = value.replace(/[^a-zA-Z]/g, "");
      setSearchText(newValue);
    },
    [setSearchText]
  );

  const onPressedEnter = React.useCallback(() => {
    if (searchText && !selectedWordsLowercase.has(searchText.toLowerCase())) {
      const newItem = {
        item: searchText,
        image_url: null,
      };
      setDeckItems([...deckItems, newItem]);
      setSearchText("");
      setExtraItem(undefined);
    }
  }, [
    searchText,
    selectedWordsLowercase,
    deckItems,
    setDeckItems,
    setSearchText,
    setExtraItem,
  ]);

  const onItemClicked = React.useCallback(
    (item: FlashpackItemForFiltering) => {
      logEvent(new FlashpackItemSelectionCellTapEvent());
      if (selectedWordsLowercase.has(item.itemLower)) {
        setPendingItemRemoval(item);
      } else {
        setDeckItems([...deckItems, item]);
      }
    },
    [setDeckItems, deckItems, selectedWordsLowercase, logEvent]
  );

  const onCancelItemRemoval = React.useCallback(
    () => setPendingItemRemoval(undefined),
    [setPendingItemRemoval]
  );

  const onConfirmItemRemoval = React.useCallback(() => {
    if (pendingItemRemoval) {
      setDeckItems(
        deckItems.filter(
          (i) => i.item.toLowerCase() !== pendingItemRemoval.item.toLowerCase()
        )
      );
      setPendingItemRemoval(undefined);
    }
  }, [pendingItemRemoval, deckItems, setDeckItems, setPendingItemRemoval]);

  return (
    <>
      <Container>
        {/* Outer container, constraining the overall width of the view based on breakpoints */}
        <Row className="justify-content-center">
          <Col xs={10} md={8} lg={6}>
            {/* Inner container, wrapping the scrolling word list row.
                WARNING: 80vh is necessary to keep the footer above the fold. */}
            <Container
              className="d-flex flex-column p-0"
              style={{ height: "80vh" }}
            >
              {minMaxCards !== undefined && (
                <Row className="justify-content-center">
                  <Col xs="auto">
                    {deckItems.length < minMaxCards.min ? (
                      <Subhead>
                        {t("flashpacks_min_cards", { count: minMaxCards.min })}
                      </Subhead>
                    ) : (
                      <Subhead>
                        {t("flashpacks_max_cards", { count: minMaxCards.max })}
                      </Subhead>
                    )}
                  </Col>
                </Row>
              )}
              {/* Selected items */}
              <Row>
                <Col>
                  <Row className="justify-content-center">
                    {deckItems.length > 0 &&
                      deckItems.map((item: FlashpackItem, index) => (
                        <ResponsiveFlashcardThumbnail
                          key={item.item}
                          onClick={() => {
                            logEvent(new FlashpackItemSelectionCardTapEvent());
                            setPendingItemRemoval(item);
                          }}
                          item={item}
                        />
                      ))}
                    {deckItems.length === 0 && (
                      <Col className="m-2">&nbsp;</Col>
                    )}
                  </Row>
                </Col>
              </Row>
              {/* Input field: Search or add words */}
              <Row
                className="justify-content-center"
                style={{
                  borderTop: `1px solid ${COLOR_GRAY_INACTIVE}`,
                  borderBottom: `1px solid ${COLOR_GRAY_INACTIVE}`,
                }}
              >
                <Col xs="auto">
                  <InputGroup className="my-1">
                    <InputGroup.Text id="search-icon">
                      <FontAwesomeIcon icon={faSearch} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder={t("flashpacks_add_or_search")}
                      aria-label="Search"
                      aria-describedby="search-icon"
                      value={searchText}
                      onChange={(e) => onChangedSearchText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          onPressedEnter();
                        }
                      }}
                    />
                    <Button variant="light" onClick={() => setSearchText("")}>
                      <FontAwesomeIcon size="1x" icon={faCircleX} />
                    </Button>
                  </InputGroup>
                </Col>
              </Row>
              {/* Scrollable list of words */}
              <Row className="flex-grow-1 overflow-hidden justify-content-center">
                <Col xs={10} className="d-flex flex-column h-100">
                  <div className="flex-grow-1 overflow-auto">
                    {selectableItems.map((item) => (
                      <FlashpackItemListItem
                        item={item}
                        isSelected={selectedWordsLowercase.has(item.itemLower)}
                        isExtraItem={false}
                        key={item.item}
                        onClick={() => onItemClicked(item)}
                        useUnknownEmptyImage={useUnknownEmptyImage}
                      />
                    ))}
                    {extraItem && (
                      <FlashpackItemListItem
                        item={extraItem}
                        isSelected={selectedWordsLowercase.has(
                          extraItem.itemLower
                        )}
                        isExtraItem={true}
                        onClick={() => onItemClicked(extraItem)}
                        useUnknownEmptyImage={useUnknownEmptyImage}
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Container>
          </Col>
        </Row>
      </Container>

      <ConfirmItemRemovalModal
        item={pendingItemRemoval}
        onCancel={onCancelItemRemoval}
        onConfirm={onConfirmItemRemoval}
      />
    </>
  );
};
